import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UnmaskingService } from '@shared/api/services/v2/unmasking.service';
import { UnmaskingStateService } from '@shared/api/services/v2/unmasking-state.service';
import { AuthService } from '@shared/services/security/auth.service';
import { SensitiveDataMaskingConfig } from '@shared/config/sensitive-data-masking.config';
import { Roles } from '@models/roles.enum';
import { Subscription } from 'rxjs';

export interface UnmaskingTarget {
  userId: number | string;
  fields: string[];
}

@Component({
  selector: 'app-batch-reveal-button',
  standalone: true,
  imports: [CommonModule],
  template: `
    <button *ngIf="canReveal && !isUnmasked"
            (click)="revealAll()"
            class="btn btn-sm btn-link p-0 ms-1 reveal-btn"
            title="Reveal all sensitive data">
      <i class="bi bi-eye"></i> Reveal All
    </button>
    <button *ngIf="canReveal && isUnmasked"
            (click)="maskAll()"
            class="btn btn-sm btn-link p-0 ms-1 reveal-btn"
            title="Mask all sensitive data">
      <i class="bi bi-eye-slash"></i> Mask All
    </button>
  `,
    styles: [`
        .reveal-btn {
            font-size: 0.8rem;
            text-decoration: none;
            vertical-align: baseline;
        }

        .reveal-btn:hover {
            text-decoration: underline;
        }
    `]
})
export class BatchRevealButtonComponent implements OnInit, OnDestroy {
  @Input() targetUserId: number | string;
  @Input() fields: string[] = [];
  @Input() targets: UnmaskingTarget[] = [];

  canReveal: boolean = false;
  isUnmasked: boolean = false;
  private subscription: Subscription;

  constructor(
    private unmaskingService: UnmaskingService,
    private unmaskingStateService: UnmaskingStateService,
    private authService: AuthService,
    private maskingConfig: SensitiveDataMaskingConfig
  ) {}

  ngOnInit() {
    // UNIVAPLAT-2789: masking temporarily deactivated platform-wide (Epic UNIVAPLAT-2776).
    // When the flag is off the component renders nothing for any role: canReveal stays
    // false (neither "Reveal All" nor "Mask All" appears) and the UnmaskingStateService
    // subscription is skipped (services stay injected but idle). Same dormancy pattern
    // as MaskedFieldComponent (UNIVAPLAT-2788).
    if (!this.maskingConfig.enabled) {
      this.canReveal = false;
      return;
    }

    const userRoles = this.authService.roles;
    this.canReveal = userRoles.includes(Roles.StudyManager);

    this.subscription = this.unmaskingStateService.getUnmaskedState().subscribe(state => {
      const allTargets = this.getAllTargets().filter(t => t.userId);
      if (allTargets.length === 0) {
        this.isUnmasked = false;
        return;
      }
      // Consider unmasked if all targets are unmasked
      this.isUnmasked = allTargets.every(target =>
        state.some(item => String(item.userId) === String(target.userId))
      );
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private getAllTargets(): UnmaskingTarget[] {
    if (this.targets && this.targets.length > 0) {
      return this.targets;
    }
    if (this.targetUserId) {
      return [{ userId: this.targetUserId, fields: this.fields }];
    }
    return [];
  }

  async revealAll() {
    // UNIVAPLAT-2789: defensive guard — with masking deactivated the button never renders,
    // but ensure unmask() can never be called.
    if (!this.maskingConfig.enabled) return;
    const allTargets = this.getAllTargets();
    if (allTargets.length === 0) return;

    try {
      for (const target of allTargets) {
        if (!target.userId || !target.fields || target.fields.length === 0) continue;
        const data = await this.unmaskingService.unmask({
          targetUserId: target.userId,
          fields: target.fields
        });
        this.unmaskingStateService.updateState(target.userId, data);
      }
    } catch (error) {
      console.error('Batch Reveal Error', error);
    }
  }

  maskAll() {
    // UNIVAPLAT-2789: defensive guard (see revealAll).
    if (!this.maskingConfig.enabled) return;
    const allTargets = this.getAllTargets().filter(t => t.userId);
    allTargets.forEach(target => {
      this.unmaskingStateService.clearState(target.userId);
    });
  }
}
